import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Zap,
  Workflow,
  Coins,
  TrendingUp,
  Bot,
  ArrowRight,
  Check,
  X,
  Play,
  Building2,
  Globe,
  ChevronRight,
  Sparkles,
  Target,
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Progress } from './ui/progress';

// ── Types ─────────────────────────────────────────────────────────────────────

type Goal = 'automate' | 'tokenize' | 'invest' | 'agency';

interface OnboardingWizardProps {
  onComplete: () => void;
  onSkip: () => void;
}

// ── Step data ─────────────────────────────────────────────────────────────────

const GOALS = [
  {
    id: 'automate' as Goal,
    icon: Workflow,
    color: 'text-blue-500',
    bg: 'bg-blue-500/10',
    border: 'border-blue-500/30',
    activeBorder: 'border-blue-500',
    activeBg: 'bg-blue-500/10',
    title: 'Automatizar procesos',
    description: 'Eliminar tareas manuales repetitivas',
    examples: 'Emails, reportes, notificaciones, sincronización de datos',
    cta: '/dashboard/workflows/new',
    ctaLabel: 'Crear primer workflow',
  },
  {
    id: 'tokenize' as Goal,
    icon: Coins,
    color: 'text-purple-500',
    bg: 'bg-purple-500/10',
    border: 'border-purple-500/30',
    activeBorder: 'border-purple-500',
    activeBg: 'bg-purple-500/10',
    title: 'Tokenizar activos',
    description: 'Convertir inmuebles o fondos en tokens',
    examples: 'Inmuebles, flotas, infraestructura, arte, participaciones',
    cta: '/dashboard/assets/new',
    ctaLabel: 'Tokenizar primer activo',
  },
  {
    id: 'invest' as Goal,
    icon: TrendingUp,
    color: 'text-green-500',
    bg: 'bg-green-500/10',
    border: 'border-green-500/30',
    activeBorder: 'border-green-500',
    activeBg: 'bg-green-500/10',
    title: 'Invertir en activos RWA',
    description: 'Comprar fracciones de activos tokenizados',
    examples: 'Desde 100€, dividendos automáticos, transparencia blockchain',
    cta: '/dashboard/infracoinn',
    ctaLabel: 'Ver activos disponibles',
  },
  {
    id: 'agency' as Goal,
    icon: Globe,
    color: 'text-orange-500',
    bg: 'bg-orange-500/10',
    border: 'border-orange-500/30',
    activeBorder: 'border-orange-500',
    activeBg: 'bg-orange-500/10',
    title: 'Escalar mi agencia',
    description: 'Automatizar servicios de clientes',
    examples: 'Social media, reportes, onboarding, facturación automática',
    cta: '/dashboard/workflows/new',
    ctaLabel: 'Crear workflow de agencia',
  },
];

const QUICK_TEMPLATES = [
  {
    id: 't1',
    name: 'Envío de ofertas semanales',
    description: 'Schedule → IA → Email a clientes',
    nodes: 5,
    time: '2 min setup',
    color: 'bg-blue-500',
    category: ['automate', 'agency'],
  },
  {
    id: 't2',
    name: 'Tokenizar inmueble',
    description: 'Wizard 5 pasos · RWA Factory · Polygon',
    nodes: 0,
    time: '10 min setup',
    color: 'bg-purple-500',
    category: ['tokenize'],
  },
  {
    id: 't3',
    name: 'Distribución de dividendos',
    description: 'Revolut → Calcular → Pagar on-chain',
    nodes: 6,
    time: '5 min setup',
    color: 'bg-green-500',
    category: ['tokenize', 'invest'],
  },
  {
    id: 't4',
    name: 'Social media automático',
    description: 'Schedule → IA → Twitter + LinkedIn',
    nodes: 7,
    time: '3 min setup',
    color: 'bg-cyan-500',
    category: ['agency', 'automate'],
  },
  {
    id: 't5',
    name: 'Reporte semanal de ROI',
    description: 'Datos → Claude AI → Email resumen',
    nodes: 4,
    time: '2 min setup',
    color: 'bg-orange-500',
    category: ['automate', 'agency'],
  },
  {
    id: 't6',
    name: 'Staking de OMX',
    description: 'MetaMask → InfraCoinn Staking → Rewards',
    nodes: 0,
    time: '5 min setup',
    color: 'bg-indigo-500',
    category: ['invest'],
  },
];

// ── Step components ───────────────────────────────────────────────────────────

function StepWelcome({ onNext }: { onNext: () => void }) {
  return (
    <div className="text-center space-y-6 py-4">
      <div className="flex justify-center">
        <img src="/logo-zaphub.png" alt="ZAPHUB" className="h-20 w-20 object-contain" />
      </div>
      <div className="space-y-2">
        <h2 className="text-2xl font-black">Bienvenido a ZAPHUB</h2>
        <p className="text-muted-foreground max-w-sm mx-auto leading-relaxed">
          En 3 minutos tendrás todo listo. Te guiamos paso a paso para que saques el máximo partido desde el primer día.
        </p>
      </div>
      <div className="grid grid-cols-3 gap-3 max-w-sm mx-auto">
        {[
          { icon: Workflow, label: 'Automatiza', color: 'text-blue-500', bg: 'bg-blue-500/10' },
          { icon: Coins, label: 'Tokeniza', color: 'text-purple-500', bg: 'bg-purple-500/10' },
          { icon: TrendingUp, label: 'Gana', color: 'text-green-500', bg: 'bg-green-500/10' },
        ].map((item) => (
          <div key={item.label} className={`rounded-xl ${item.bg} p-3 text-center`}>
            <item.icon className={`h-6 w-6 ${item.color} mx-auto mb-1`} />
            <p className="text-xs font-semibold">{item.label}</p>
          </div>
        ))}
      </div>
      <Button
        size="lg"
        className="bg-primary hover:bg-primary/90 px-10 font-semibold"
        onClick={onNext}
      >
        Empezar
        <ArrowRight className="ml-2 h-4 w-4" />
      </Button>
      <p className="text-xs text-muted-foreground">3 minutos · Sin configuración compleja</p>
    </div>
  );
}

function StepGoal({ onNext, selectedGoal, onSelectGoal }: {
  onNext: () => void;
  selectedGoal: Goal | null;
  onSelectGoal: (g: Goal) => void;
}) {
  return (
    <div className="space-y-5">
      <div className="text-center space-y-1">
        <h2 className="text-xl font-black">¿Cuál es tu objetivo principal?</h2>
        <p className="text-sm text-muted-foreground">Personalizamos tu experiencia según lo que quieras lograr</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        {GOALS.map((goal) => {
          const isSelected = selectedGoal === goal.id;
          return (
            <button
              key={goal.id}
              className={`
                text-left rounded-xl border p-4 transition-all duration-150 w-full
                ${isSelected
                  ? `${goal.activeBorder} ${goal.activeBg} ring-1 ring-inset ${goal.activeBorder}`
                  : `border-border hover:border-muted-foreground/30 bg-card`
                }
              `}
              onClick={() => onSelectGoal(goal.id)}
            >
              <div className="flex items-start gap-3">
                <div className={`h-9 w-9 rounded-lg ${goal.bg} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                  <goal.icon className={`h-5 w-5 ${goal.color}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-semibold text-sm">{goal.title}</p>
                    {isSelected && <Check className={`h-4 w-4 ${goal.color} flex-shrink-0`} />}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{goal.description}</p>
                  <p className="text-[10px] text-muted-foreground/60 mt-1 leading-relaxed">{goal.examples}</p>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <Button
        className="w-full bg-primary hover:bg-primary/90 font-semibold"
        disabled={!selectedGoal}
        onClick={onNext}
      >
        Continuar
        <ArrowRight className="ml-2 h-4 w-4" />
      </Button>
    </div>
  );
}

function StepTemplate({ onNext, selectedGoal }: { onNext: (templateId: string) => void; selectedGoal: Goal | null }) {
  const [selected, setSelected] = useState<string | null>(null);

  const relevant = QUICK_TEMPLATES.filter(t =>
    !selectedGoal || t.category.includes(selectedGoal)
  );

  return (
    <div className="space-y-5">
      <div className="text-center space-y-1">
        <h2 className="text-xl font-black">Elige una plantilla de inicio</h2>
        <p className="text-sm text-muted-foreground">O empieza desde cero si prefieres</p>
      </div>

      <div className="grid sm:grid-cols-2 gap-2.5 max-h-72 overflow-y-auto pr-1">
        {relevant.map((t) => (
          <button
            key={t.id}
            className={`
              text-left rounded-xl border p-3.5 transition-all duration-150 w-full
              ${selected === t.id
                ? 'border-primary bg-primary/5 ring-1 ring-inset ring-primary'
                : 'border-border hover:border-muted-foreground/30 bg-card'
              }
            `}
            onClick={() => setSelected(t.id)}
          >
            <div className="flex items-start gap-3">
              <div className={`h-8 w-8 rounded-lg ${t.color} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                <Sparkles className="h-4 w-4 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-1">
                  <p className="font-semibold text-sm truncate">{t.name}</p>
                  {selected === t.id && <Check className="h-4 w-4 text-primary flex-shrink-0" />}
                </div>
                <p className="text-[11px] text-muted-foreground mt-0.5 leading-relaxed">{t.description}</p>
                <div className="flex items-center gap-2 mt-1.5">
                  {t.nodes > 0 && (
                    <Badge variant="secondary" className="text-[9px] px-1.5 py-0 h-4">{t.nodes} nodos</Badge>
                  )}
                  <Badge variant="secondary" className="text-[9px] px-1.5 py-0 h-4">{t.time}</Badge>
                </div>
              </div>
            </div>
          </button>
        ))}
      </div>

      <div className="flex gap-2">
        <Button
          variant="outline"
          className="flex-1"
          onClick={() => onNext('blank')}
        >
          Empezar en blanco
        </Button>
        <Button
          className="flex-1 bg-primary hover:bg-primary/90 font-semibold"
          disabled={!selected}
          onClick={() => onNext(selected!)}
        >
          Usar plantilla
          <ArrowRight className="ml-2 h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

function StepComplete({ selectedGoal, onComplete }: { selectedGoal: Goal | null; onComplete: () => void }) {
  const navigate = useNavigate();
  const goal = GOALS.find(g => g.id === selectedGoal) || GOALS[0];

  const handleAction = () => {
    onComplete();
    navigate(goal.cta);
  };

  return (
    <div className="text-center space-y-6 py-2">
      <div className="flex justify-center">
        <div className="h-16 w-16 rounded-full bg-green-500/10 flex items-center justify-center">
          <Check className="h-8 w-8 text-green-500" />
        </div>
      </div>

      <div className="space-y-2">
        <h2 className="text-xl font-black">¡Todo listo!</h2>
        <p className="text-sm text-muted-foreground max-w-xs mx-auto">
          Tu cuenta está configurada. Ahora dale al primer paso.
        </p>
      </div>

      <div className={`rounded-xl border ${goal.border} ${goal.bg} p-4 space-y-2 max-w-xs mx-auto`}>
        <div className="flex items-center gap-2 justify-center">
          <goal.icon className={`h-5 w-5 ${goal.color}`} />
          <p className="font-semibold text-sm">{goal.title}</p>
        </div>
        <p className="text-xs text-muted-foreground">{goal.description}</p>
      </div>

      <div className="space-y-2 max-w-xs mx-auto">
        <Button
          size="lg"
          className="w-full bg-primary hover:bg-primary/90 font-semibold"
          onClick={handleAction}
        >
          <Target className="mr-2 h-4 w-4" />
          {goal.ctaLabel}
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="w-full text-muted-foreground"
          onClick={onComplete}
        >
          Explorar el dashboard primero
        </Button>
      </div>

      {/* Quick links */}
      <div className="border-t border-border pt-4 space-y-2 max-w-xs mx-auto">
        <p className="text-xs text-muted-foreground font-medium">También puedes explorar:</p>
        <div className="grid grid-cols-2 gap-2">
          {[
            { label: 'Ver workflows', path: '/dashboard/workflows', icon: Workflow },
            { label: 'Agentes IA', path: '/dashboard/agents', icon: Bot },
            { label: 'InfraCoinn', path: '/dashboard/infracoinn', icon: Coins },
            { label: 'Analytics', path: '/dashboard/analytics', icon: TrendingUp },
          ].map((link) => {
            const nav = useNavigate();
            return (
              <button
                key={link.path}
                className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors p-2 rounded-lg hover:bg-accent text-left"
                onClick={() => { onComplete(); nav(link.path); }}
              >
                <link.icon className="h-3.5 w-3.5 flex-shrink-0" />
                {link.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── Main Wizard ───────────────────────────────────────────────────────────────

const STEP_LABELS = ['Bienvenida', 'Objetivo', 'Plantilla', 'Listo'];

export function OnboardingWizard({ onComplete, onSkip }: OnboardingWizardProps) {
  const [step, setStep] = useState(0);
  const [selectedGoal, setSelectedGoal] = useState<Goal | null>(null);

  const progress = (step / (STEP_LABELS.length - 1)) * 100;

  return (
    <div className="fixed inset-0 z-50 bg-background/80 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-card border border-border rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden">
        {/* Header */}
        <div className="border-b border-border px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/logo-zaphub.png" alt="ZAPHUB" className="h-6 w-6 object-contain" />
            <span className="font-bold text-sm">Configuración inicial</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-muted-foreground">{STEP_LABELS[step]}</span>
            <button
              onClick={onSkip}
              className="text-muted-foreground hover:text-foreground transition-colors"
              title="Saltar configuración"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Progress */}
        <div className="px-6 pt-4">
          <div className="flex items-center gap-1.5 mb-4">
            {STEP_LABELS.map((label, idx) => (
              <div key={label} className="flex items-center gap-1.5 flex-1">
                <div className={`
                  h-6 w-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 transition-all
                  ${idx < step ? 'bg-primary text-primary-foreground' :
                    idx === step ? 'bg-primary/20 text-primary border border-primary' :
                    'bg-muted text-muted-foreground'}
                `}>
                  {idx < step ? <Check className="h-3 w-3" /> : idx + 1}
                </div>
                {idx < STEP_LABELS.length - 1 && (
                  <div className={`h-0.5 flex-1 rounded-full transition-all ${idx < step ? 'bg-primary' : 'bg-muted'}`} />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Step content */}
        <div className="px-6 pb-6">
          {step === 0 && (
            <StepWelcome onNext={() => setStep(1)} />
          )}
          {step === 1 && (
            <StepGoal
              onNext={() => setStep(2)}
              selectedGoal={selectedGoal}
              onSelectGoal={setSelectedGoal}
            />
          )}
          {step === 2 && (
            <StepTemplate
              onNext={() => setStep(3)}
              selectedGoal={selectedGoal}
            />
          )}
          {step === 3 && (
            <StepComplete
              selectedGoal={selectedGoal}
              onComplete={onComplete}
            />
          )}
        </div>
      </div>
    </div>
  );
}
